import type { JSX } from 'react'
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  CircleX,
  Info,
  House,
  AppWindow,
  ShieldCheck,
  LogOut,
  Menu,
  Moon,
  Sun,
  TriangleAlert,
  X,
  type LucideProps
} from 'lucide-react'

/**
 * The glyphs this console renders.
 *
 * Registered explicitly rather than re-exporting all of lucide: a namespace
 * import defeats tree-shaking and pulls the whole icon set into the bundle.
 * Add a row when a screen needs a glyph.
 */
const REGISTRY = {
  home: House,
  'app-window': AppWindow,
  'shield-check': ShieldCheck,
  info: Info,
  'triangle-alert': TriangleAlert,
  'circle-x': CircleX,
  'log-out': LogOut,
  menu: Menu,
  x: X,
  // The date-range trigger, the table's sort marks and the pager's four steps.
  'calendar-days': CalendarDays,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'chevrons-up-down': ChevronsUpDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevrons-left': ChevronsLeft,
  'chevrons-right': ChevronsRight,
  // The theme toggle. Each glyph names the theme a press SWITCHES TO, not the
  // one on screen, which is why the control shows a moon in light.
  sun: Sun,
  moon: Moon
} as const

export type IconName = keyof typeof REGISTRY

export interface IconProps extends Omit<LucideProps, 'ref'> {
  name: IconName
}

/**
 * Icon — a lucide glyph.
 *
 * Hidden from assistive technology: a glyph never carries the meaning on its
 * own, so its label lives on the control that contains it.
 *
 * The only wrapper here that does **not** require `data-testid`, deliberately:
 * an `aria-hidden` decoration is never a test target, and the control around
 * it already carries the hook.
 *
 * @param props - Icon props.
 * @param props.name - Registered glyph name.
 * @returns The rendered glyph.
 */
export function Icon({ name, ...props }: IconProps): JSX.Element {
  const Glyph = REGISTRY[name]

  return <Glyph aria-hidden="true" {...props} />
}
